import isNumber from 'lodash-es/isNumber';
import { BoxedHashtags } from 'mk2/apps/blogs/components/HashtagsBox';
import {
    BlogsFeedFetchFailureAction,
    BlogsFeedFetchRequestAction,
    BlogsFeedFetchSuccessAction,
    BLOGS_FEED_FETCH_FAILURE,
    BLOGS_FEED_FETCH_REQUEST,
    BLOGS_FEED_FETCH_SUCCESS,
} from 'mk2/apps/blogs/containers/BlogsFeed/BlogsFeed.actions';
import {
    CommentsFetchFailureAction,
    CommentsFetchRequestAction,
    CommentsFetchSuccessAction,
    COMMENTS_FETCH_FAILURE,
    COMMENTS_FETCH_REQUEST,
    COMMENTS_FETCH_SUCCESS,
} from 'mk2/apps/blogs/containers/Comments/Comments.actions';
import { tupdate } from 'mk2/helpers/types';
import { errorMessage } from 'mk2/reducers/errorMessage';
import { loadingState, LoadingState } from 'mk2/reducers/loadingState';
import { Reducer } from 'redux';

export interface BlogsFeedPageState {
    brandDaysPostId: number;
    postIds: number[];
    postsSubtreeComplaintList?: {[postId: number]: number[]};
    errorMessage: string;
    loadingState: LoadingState;
    loadedAt: number;
}

export const initialBlogsFeedPageState: BlogsFeedPageState = {
    brandDaysPostId: null,
    postIds: [],
    errorMessage: null,
    loadingState: LoadingState.INIT,
    loadedAt: null,
};

export interface BlogsFeedVirtualPageState {
    errorMessage: string;
    loadingState: LoadingState;
}

export const initialBlogsFeedVirtualPageState: BlogsFeedVirtualPageState = {
    errorMessage: null,
    loadingState: LoadingState.INIT,
};

export interface PostsState {
    pages: {[pageNum: number]: BlogsFeedPageState};
    virtualPages: {[pageName: string]: BlogsFeedVirtualPageState};  // pageName = 'first'|'last'...
}

export const initialPostsState: PostsState = {
    pages: {},
    virtualPages: {},
};

type PostsReducerAction =
    BlogsFeedFetchRequestAction
    | BlogsFeedFetchSuccessAction
    | BlogsFeedFetchFailureAction;

const postsReducer: Reducer<PostsState> = (state = initialPostsState, action: PostsReducerAction) => {
    switch (action.type) {
        case BLOGS_FEED_FETCH_REQUEST:
        case BLOGS_FEED_FETCH_FAILURE:
            if (isNumber(action.pageRef)) {
                return tupdate(state, {
                    pages: tupdate(state.pages, {
                        [action.pageRef]: tupdate(state.pages[action.pageRef] || initialBlogsFeedPageState, {
                            loadingState: loadingState(undefined, action),
                            errorMessage: errorMessage(undefined, action),
                        }),
                    }),
                });
            } else {
                return tupdate(state, {
                    virtualPages: tupdate(state.virtualPages, {
                        [action.pageRef]: tupdate(state.pages[action.pageRef] || initialBlogsFeedVirtualPageState, {
                            loadingState: loadingState(undefined, action),
                            errorMessage: errorMessage(undefined, action),
                        }),
                    }),
                });
            }

        case BLOGS_FEED_FETCH_SUCCESS:
            if (!isNumber(action.page)) {
                throw new Error('Page must be a number');
            }

            return tupdate(state, {
                // update state of page under both - virtual name and real index
                virtualPages: isNumber(action.pageRef) ? state.virtualPages : tupdate(state.virtualPages, {
                    [action.pageRef]: tupdate(state.virtualPages[action.pageRef] || initialBlogsFeedVirtualPageState, {
                        loadingState: loadingState(undefined, action),
                        errorMessage: errorMessage(undefined, action),
                    }),
                }),
                pages: tupdate(state.pages, {
                    [action.page]: tupdate(state.pages[action.page] || initialBlogsFeedPageState, {
                        brandDaysPostId: action.brandDaysPostId,
                        postIds: action.response.result.posts,
                        postsSubtreeComplaintList: action.response.result.postsSubtreeComplaintList,
                        loadingState: loadingState(undefined, action),
                        errorMessage: errorMessage(undefined, action),
                        loadedAt: new Date().getTime(),
                    }),
                }),
            });

        default:
            return state;
    }
};

interface BlogsFeedByQueryState {
    [query: string]: PostsState;
}

export const queryKey = (category, postType, query): string => `${category || 'all'}:${postType}:posts:${query || ''}`;

const searchKey = (action: (BlogsFeedFetchRequestAction | BlogsFeedFetchSuccessAction | BlogsFeedFetchFailureAction)) =>
    queryKey(action.category, action.postType, action.query);

export interface BlogsFeedState {
    commentsLoadingState: LoadingState;
    errorMessage: string;
    hasComplaints: boolean;
    hashtags: BoxedHashtags;
    searches: BlogsFeedByQueryState;
}

export const initialBlogsFeedState: BlogsFeedState = {
    commentsLoadingState: LoadingState.INIT,
    errorMessage: null,
    hasComplaints: false,
    hashtags: {
        newest: [],
        mostUsed: [],
    },
    searches: {},
};

declare type BlogsFeedReducerAction =
    CommentsFetchRequestAction
    | CommentsFetchSuccessAction
    | CommentsFetchFailureAction
    | BlogsFeedFetchRequestAction
    | BlogsFeedFetchSuccessAction
    | BlogsFeedFetchFailureAction;

const blogsFeedReducer: Reducer<BlogsFeedState> = (state = initialBlogsFeedState, action: BlogsFeedReducerAction) => {
    switch (action.type) {
        case COMMENTS_FETCH_REQUEST:
        case COMMENTS_FETCH_SUCCESS:
        case COMMENTS_FETCH_FAILURE:
            return tupdate(state, {
                commentsLoadingState: loadingState(undefined, action),
            });

        case BLOGS_FEED_FETCH_REQUEST:
        case BLOGS_FEED_FETCH_FAILURE:
            return tupdate(state, {
                searches: tupdate(state.searches, {
                    [searchKey(action)]: postsReducer(state.searches[searchKey(action)], action),
                }),
            });

        case BLOGS_FEED_FETCH_SUCCESS:
            return tupdate(state, {
                searches: tupdate(state.searches, {
                    [searchKey(action)]: postsReducer(state.searches[searchKey(action)], action),
                }),
                hasComplaints: action.hasComplaints,
                hashtags: action.hashtags,
            });

        default:
            return state;
    }
};

export default blogsFeedReducer;
